import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ExternalLink } from '../../auth/types';
import { client } from '../generated/client.gen';
import type { AdminMember } from './admin';
import { refreshMe } from './me';

// /api/v1/team/* — tenant-admin surface. The tenant id comes from the
// X-Tenant-Id header (set by the request interceptor in api/client.ts), so
// the query key doesn't include it; queryClient.clear() runs on tenant switch
// and drops these entries.
const teamMembersKey = ['team', 'members'] as const;
const teamExternalLinksKey = ['team', 'external-links'] as const;

function useTeamMembers(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: teamMembersKey,
    enabled: opts?.enabled ?? true,
    queryFn: async (): Promise<AdminMember[]> => {
      const { data } = await client.instance.get<{ data: AdminMember[] }>('/team');
      return data.data;
    },
  });
}

function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ email }: { email: string }): Promise<AdminMember> => {
      const { data } = await client.instance.post<AdminMember>('/team/invites', { email });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey });
    },
  });
}

function useDeactivateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ membershipId }: { membershipId: string }): Promise<AdminMember> => {
      const { data } = await client.instance.patch<AdminMember>(`/team/${membershipId}`, {
        status: 'disabled',
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey });
    },
  });
}

function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ membershipId }: { membershipId: string }): Promise<void> => {
      await client.instance.delete(`/team/${membershipId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: teamMembersKey });
    },
  });
}

function useTeamExternalLinks() {
  return useQuery({
    queryKey: teamExternalLinksKey,
    queryFn: async (): Promise<ExternalLink[]> => {
      const { data } = await client.instance.get<{ data: ExternalLink[] }>('/team/external-links');
      return data.data;
    },
  });
}

function useUpdateTeamExternalLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (externalLinks: ExternalLink[]): Promise<ExternalLink[]> => {
      const { data } = await client.instance.put<{ data: ExternalLink[] }>('/team/external-links', {
        externalLinks,
      });
      return data.data;
    },
    onSuccess: (links) => {
      qc.setQueryData(teamExternalLinksKey, links);
      void refreshMe().catch(() => undefined);
    },
  });
}

export const teamApi = {
  useTeamMembers,
  useInviteTeamMember,
  useDeactivateTeamMember,
  useRemoveTeamMember,
  useTeamExternalLinks,
  useUpdateTeamExternalLinks,
};
